
import { RevenueScenario, revenueSplits } from '@/components/saas/landing/business-opportunity/data';
import { subscriptionPlans, SubscriptionPlan } from '@/types/subscriptionPlans';

// Monthly revenue data for business plans
export const monthlyRevenueData = [
  { month: 'Jan', revenue: 12450, newSales: 8, activeSales: 42 },
  { month: 'Feb', revenue: 14230, newSales: 10, activeSales: 52 },
  { month: 'Mar', revenue: 18760, newSales: 14, activeSales: 64 },
  { month: 'Apr', revenue: 21340, newSales: 16, activeSales: 79 },
  { month: 'May', revenue: 24890, newSales: 19, activeSales: 96 },
  { month: 'Jun', revenue: 28450, newSales: 22, activeSales: 116 },
  { month: 'Jul', revenue: 32180, newSales: 24, activeSales: 138 },
  { month: 'Aug', revenue: 35670, newSales: 28, activeSales: 163 },
  { month: 'Sep', revenue: 39240, newSales: 30, activeSales: 190 },
  { month: 'Oct', revenue: 42780, newSales: 32, activeSales: 219 },
  { month: 'Nov', revenue: 46320, newSales: 35, activeSales: 251 },
  { month: 'Dec', revenue: 49870, newSales: 38, activeSales: 285 }
];

export interface BusinessPlanSubscription {
  id: string;
  planId: string;
  planName: string;
  customerName: string;
  customerEmail: string;
  price: number;
  status: 'active' | 'trial' | 'cancelled' | 'failed';
  billingCycle: 'monthly' | 'quarterly' | 'yearly';
  startDate: Date;
  nextBillingDate?: Date;
  licenseKeyId?: string;
  licenseKey?: string;
  superAdminId?: string;
  superAdminName?: string;
}

// Generate sample business plan subscriptions based on the business signup plans
export const generateBusinessPlanSubscriptions = (count: number = 100): BusinessPlanSubscription[] => {
  const subscriptions: BusinessPlanSubscription[] = [];
  const statuses: ('active' | 'trial' | 'cancelled' | 'failed')[] = ['active', 'trial', 'cancelled', 'failed'];
  const billingCycles: ('monthly' | 'quarterly' | 'yearly')[] = ['monthly', 'quarterly', 'yearly'];
  const licenseKeyPrefixes = ['RMBZ-', 'RMBP-', 'RMBC-'];
  
  // Get business plans from localStorage or use default plans
  let businessPlans: SubscriptionPlan[] = subscriptionPlans;
  try {
    const savedPlans = localStorage.getItem('business_signup_plans');
    if (savedPlans) {
      businessPlans = JSON.parse(savedPlans);
    }
  } catch (error) {
    console.error('Error loading business plans from localStorage:', error);
  }
  
  for (let i = 0; i < count; i++) {
    // Select a random business plan
    const planIndex = Math.floor(Math.random() * businessPlans.length);
    const plan = businessPlans[planIndex];
    
    // Generate a random date within the last year
    const startDate = new Date();
    startDate.setFullYear(startDate.getFullYear() - 1);
    startDate.setDate(startDate.getDate() + Math.floor(Math.random() * 365));
    
    // Generate a next billing date (30 days after start date)
    const nextBillingDate = new Date(startDate);
    nextBillingDate.setDate(nextBillingDate.getDate() + 30);
    
    // Generate a license key if the subscription is active
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const licenseKeyId = status === 'active' || status === 'trial' ? `lic-${i + 1000}` : undefined;
    const licenseKey = licenseKeyId ? 
      `${licenseKeyPrefixes[Math.floor(Math.random() * licenseKeyPrefixes.length)]}${Math.random().toString(36).substr(2, 10).toUpperCase()}-${Math.random().toString(36).substr(2, 10).toUpperCase()}` : 
      undefined;
    
    // Generate a super admin ID and name if there's a license key
    const superAdminId = licenseKeyId ? `sa-${i + 2000}` : undefined;
    const superAdminName = superAdminId ? `Super Admin ${i + 1}` : undefined;
    
    // Determine price based on billing cycle
    let price = plan.monthlyPrice;
    const billingCycle = billingCycles[Math.floor(Math.random() * billingCycles.length)];
    if (billingCycle === 'quarterly') {
      price = plan.quarterlyPrice;
    } else if (billingCycle === 'yearly') {
      price = plan.yearlyPrice || (plan.monthlyPrice * 12 * 0.8); // 20% discount if yearlyPrice not specified
    }
    
    subscriptions.push({
      id: `sub-${i + 1}`,
      planId: plan.id,
      planName: plan.name,
      customerName: `Business ${i + 1}`,
      customerEmail: `business${i + 1}@example.com`,
      price,
      status,
      billingCycle,
      startDate,
      nextBillingDate: status === 'active' ? nextBillingDate : undefined,
      licenseKeyId,
      licenseKey,
      superAdminId,
      superAdminName
    });
  }
  
  return subscriptions;
};
